/* BloomVault Shopify theme — interactive layer (v2 stable rebuild)
 *
 * Design principles after the iOS Safari debugging round:
 *  1. CSS-first. The page must render correctly with JS disabled. JS only
 *     adds enhancements (tickers, scroll progress, calculator math, etc.).
 *  2. No element is pre-hidden by JS waiting on an IntersectionObserver.
 *     If JS never runs, EVERY element on the page is still visible.
 *  3. No perpetual rAF loops. All animations are either CSS @keyframes,
 *     IO-once-and-done, or interval-based with idle pauses.
 *  4. Touch devices skip mouse-driven animations entirely (magnetic,
 *     floating mockups). Saves Safari iOS main-thread budget.
 *  5. Every init is wrapped in try/catch. One broken module can't take
 *     down the rest.
 */

(function () {
  'use strict';

  // -----------------------------------------------------------------
  // Utilities
  // -----------------------------------------------------------------
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));
  const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
  const ease = {
    outExpo: (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
  };

  function formatMoney(cents, currency) {
    const c = cents / 100;
    if (!currency || currency === 'USD') {
      return c % 1 === 0 ? '$' + c : '$' + c.toFixed(2);
    }
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      }).format(c);
    } catch (e) {
      return '$' + c.toFixed(2);
    }
  }

  // -----------------------------------------------------------------
  // Mobile menu — JS ENHANCEMENT ONLY.
  //
  // The menu itself is driven by the pure-CSS checkbox-hack in
  // sections/header.liquid. This function only:
  //   - Closes the menu when an in-page anchor link is tapped.
  //   - Mirrors the checked state to aria-expanded.
  //   - Closes on Escape.
  //
  // If this entire function throws, the menu still works.
  // -----------------------------------------------------------------
  function initMobileMenu() {
    const checkbox = $('#bv-menu-state');
    const trigger = $('#bv-menu-trigger');
    if (!checkbox || !trigger) return;

    const syncAria = () => {
      trigger.setAttribute('aria-expanded', checkbox.checked ? 'true' : 'false');
      trigger.setAttribute('aria-label', checkbox.checked ? 'Close menu' : 'Open menu');
    };
    syncAria();
    checkbox.addEventListener('change', syncAria);

    $$('[data-bv-menu-link]').forEach((link) => {
      link.addEventListener('click', () => {
        // Uncheck after a tick so the in-page anchor jump can run first.
        setTimeout(() => {
          checkbox.checked = false;
          syncAria();
        }, 60);
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && checkbox.checked) {
        checkbox.checked = false;
        syncAria();
      }
    });
  }

  // -----------------------------------------------------------------
  // Nav — scroll-based background fade. No backdrop-filter anywhere
  // (Safari iOS chokes on per-frame backdrop-filter updates).
  // -----------------------------------------------------------------
  function initNav() {
    const nav = $('[data-bv-nav]');
    if (!nav) return;
    const bg = $('[data-bv-nav-bg]', nav);
    const border = $('[data-bv-nav-border]', nav);
    const update = () => {
      const y = window.scrollY;
      const t = clamp(y / 80, 0, 1);
      if (bg) bg.style.opacity = String(t * 0.92);
      if (border) border.style.opacity = String(t);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  // -----------------------------------------------------------------
  // Fade-in for sections below the fold.
  //
  // CRITICAL: elements are NOT pre-hidden by JS. If the observer never
  // fires (Safari quirk, JS error, slow CPU), elements stay visible at
  // their natural opacity. We only ADD a one-shot fade when the element
  // enters the viewport for the first time.
  // -----------------------------------------------------------------
  function initFadeIn() {
    const items = $$('[data-bv-fade]');
    if (!items.length || !('IntersectionObserver' in window)) return;

    // Pre-set transition properties so when we change opacity/transform
    // there's a smooth animation. But opacity STAYS at 1 until intersect.
    items.forEach((el) => {
      el.style.willChange = 'opacity, transform';
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const delay = parseFloat(el.dataset.bvDelay || '0');
          // Animate FROM opacity:0 TO opacity:1 using a temporary animation,
          // not by mutating inline opacity first. This means if the rAF gets
          // dropped, the element stays at its natural opacity:1.
          el.style.animation = `bvFadeUp 0.6s cubic-bezier(0.21,0.47,0.32,0.98) ${delay}s both`;
          io.unobserve(el);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' },
    );
    items.forEach((el) => io.observe(el));
  }

  // -----------------------------------------------------------------
  // Floating mockups — DESKTOP ONLY, FINE POINTER ONLY.
  // -----------------------------------------------------------------
  function initFloatingMockups() {
    if (reduceMotion || !isFinePointer) return;
    const stage = $('[data-bv-mockups]');
    if (!stage) return;
    if (getComputedStyle(stage).display === 'none') return;
    const items = $$('[data-bv-mockup]', stage);
    if (!items.length) return;

    // Initial entrance — one-shot CSS animation.
    items.forEach((el, i) => {
      const baseRotate = parseFloat(el.dataset.bvRotate || '0');
      el.style.opacity = '0';
      el.style.transform = `translateY(60px) scale(0.92) rotate(${baseRotate}deg)`;
      el.style.transition = `opacity 0.8s ease ${0.3 + i * 0.15}s, transform 0.8s cubic-bezier(0.21,0.47,0.32,0.98) ${0.3 + i * 0.15}s`;
      requestAnimationFrame(() => {
        el.style.opacity = '1';
        el.style.transform = `translateY(0) scale(1) rotate(${baseRotate}deg)`;
      });
    });

    // Lightweight mousemove parallax — single rAF coalesced.
    let lastX = 0, lastY = 0, pending = false;
    window.addEventListener('mousemove', (e) => {
      lastX = (e.clientX / window.innerWidth - 0.5) * 2;
      lastY = (e.clientY / window.innerHeight - 0.5) * 2;
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        items.forEach((el) => {
          const rangeX = parseFloat(el.dataset.bvRangeX || '20');
          const rangeY = parseFloat(el.dataset.bvRangeY || '20');
          const baseRotate = parseFloat(el.dataset.bvRotate || '0');
          el.style.transform = `translate(${lastX * rangeX}px, ${lastY * rangeY}px) rotate(${baseRotate}deg)`;
        });
        pending = false;
      });
    }, { passive: true });
  }

  // -----------------------------------------------------------------
  // Number tickers — IO-once-and-done.
  // -----------------------------------------------------------------
  function initNumberTickers() {
    const items = $$('[data-bv-ticker]');
    if (!items.length || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const target = parseFloat(el.dataset.bvTicker);
          const decimals = parseInt(el.dataset.bvDecimals || '0', 10);
          const suffix = el.dataset.bvSuffix || '';
          const duration = parseFloat(el.dataset.bvDuration || '1.6') * 1000;
          const start = performance.now();
          const fmt = (n) =>
            (decimals > 0 ? n.toFixed(decimals) : Math.round(n).toLocaleString('en-US')) + suffix;
          const tick = (now) => {
            const t = clamp((now - start) / duration, 0, 1);
            const v = ease.outExpo(t) * target;
            el.textContent = fmt(v);
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          io.unobserve(el);
        });
      },
      { threshold: 0.4 },
    );
    items.forEach((el) => io.observe(el));
  }

  // -----------------------------------------------------------------
  // Marquees — duplicate innerHTML for seamless loop.
  // -----------------------------------------------------------------
  function initMarquees() {
    $$('[data-bv-marquee]').forEach((el) => {
      if (el.dataset.bvMarqueeReady) return;
      el.dataset.bvMarqueeReady = '1';
      const html = el.innerHTML;
      el.innerHTML = html + html;
    });
  }

  // -----------------------------------------------------------------
  // Scroll progress bar.
  // -----------------------------------------------------------------
  function initScrollProgress() {
    const bar = $('#bv-scroll-progress > div');
    if (!bar) return;
    const update = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      const pct = total > 0 ? window.scrollY / total : 0;
      bar.style.transform = `scaleX(${clamp(pct, 0, 1)})`;
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
  }

  // -----------------------------------------------------------------
  // Sticky mobile CTA — appears after first scroll.
  // -----------------------------------------------------------------
  function initStickyMobileCta() {
    const el = document.getElementById('bv-sticky-mobile');
    if (!el) return;
    const update = () => {
      const y = window.scrollY;
      const t = clamp((y - 200) / 280, 0, 1);
      el.style.opacity = String(t);
      el.style.transform = `translateY(${(1 - t) * 40}px)`;
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  // -----------------------------------------------------------------
  // BigDomino — sigil spin + scroll-parallax quote (desktop only).
  // -----------------------------------------------------------------
  function initBigDomino() {
    const section = $('[data-bv-big-domino]');
    if (!section || reduceMotion) return;
    const sigil = $('[data-bv-sigil]', section);
    if (sigil) sigil.style.animation = 'bvSpin 120s linear infinite';
    const quote = $('[data-bv-domino-quote]', section);
    if (!quote) return;
    if (window.matchMedia('(max-width: 1023px)').matches) return;
    const update = () => {
      const r = section.getBoundingClientRect();
      const total = r.height + window.innerHeight;
      const passed = -r.top + window.innerHeight;
      const t = clamp(passed / total, 0, 1);
      const x = -40 + 80 * t;
      quote.style.transform = `translateX(${x}px)`;
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
  }

  // -----------------------------------------------------------------
  // Showcase — category filters + desktop 3D tilt on hover.
  // -----------------------------------------------------------------
  function initShowcase() {
    const grid = $('[data-bv-showcase-grid]');
    if (!grid) return;
    const cards = $$('[data-bv-product]', grid);
    const buttons = $$('[data-bv-filter]');
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const filter = btn.dataset.bvFilter;
        buttons.forEach((b) => b.classList.remove('bv-filter-active'));
        btn.classList.add('bv-filter-active');
        cards.forEach((card) => {
          const cat = card.dataset.bvCategory;
          const badge = card.dataset.bvBadge;
          let show = false;
          if (filter === 'all') show = true;
          else if (filter === 'trending') show = badge === 'trending';
          else show = cat === filter;
          card.style.display = show ? '' : 'none';
        });
      });
    });
    if (!reduceMotion && isFinePointer) {
      cards.forEach((card) => {
        card.addEventListener('mousemove', (e) => {
          const r = card.getBoundingClientRect();
          const x = (e.clientX - r.left) / r.width - 0.5;
          const y = (e.clientY - r.top) / r.height - 0.5;
          card.style.transform = `perspective(1000px) rotateY(${-x * 6}deg) rotateX(${y * 6}deg) translateY(-4px)`;
        });
        card.addEventListener('mouseleave', () => { card.style.transform = ''; });
      });
    }
  }

  // -----------------------------------------------------------------
  // TimeMath — hover-reveal breakdown rows (desktop only).
  // -----------------------------------------------------------------
  function initTimeMath() {
    $$('[data-bv-timemath-item]').forEach((li) => {
      const breakdown = $('[data-bv-breakdown]', li);
      const clock = $('[data-bv-clock]', li);
      const sunk = $('[data-bv-sunk]', li);
      if (!breakdown) return;
      breakdown.style.maxHeight = '0';
      breakdown.style.opacity = '0';
      breakdown.style.overflow = 'hidden';
      breakdown.style.transition = 'max-height 0.4s ease, opacity 0.3s ease';
      const show = () => {
        breakdown.style.maxHeight = breakdown.scrollHeight + 'px';
        breakdown.style.opacity = '1';
        if (clock) clock.style.transform = 'rotate(360deg)';
        if (sunk) sunk.style.opacity = '1';
      };
      const hide = () => {
        breakdown.style.maxHeight = '0';
        breakdown.style.opacity = '0';
        if (clock) clock.style.transform = 'rotate(0deg)';
        if (sunk) sunk.style.opacity = '0';
      };
      li.addEventListener('mouseenter', show);
      li.addEventListener('mouseleave', hide);
    });
  }

  // -----------------------------------------------------------------
  // DropSystem — surge ticker + countdown to next Friday 14:00 UTC.
  // -----------------------------------------------------------------
  function initDropSystem() {
    const surgeWrap = $('[data-bv-surge]');
    if (surgeWrap) {
      const items = surgeWrap.dataset.bvSurges ? JSON.parse(surgeWrap.dataset.bvSurges) : [];
      let idx = 0;
      const out = $('[data-bv-surge-text]', surgeWrap);
      const cycle = () => {
        if (!out || !items.length) return;
        out.style.transition = 'opacity 0.3s, transform 0.3s';
        out.style.opacity = '0';
        out.style.transform = 'translateY(-12px)';
        setTimeout(() => {
          idx = (idx + 1) % items.length;
          out.textContent = items[idx];
          out.style.transform = 'translateY(12px)';
          requestAnimationFrame(() => {
            out.style.opacity = '1';
            out.style.transform = 'translateY(0)';
          });
        }, 300);
      };
      if (out && items.length) {
        out.textContent = items[0];
        setInterval(cycle, 2200);
      }
    }
    const cd = $('[data-bv-countdown]');
    if (cd) {
      const compute = () => {
        const now = new Date();
        const target = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 14, 0, 0));
        const dayUTC = now.getUTCDay();
        let add = (5 - dayUTC + 7) % 7;
        if (add === 0 && now.getUTCHours() >= 14) add = 7;
        target.setUTCDate(target.getUTCDate() + add);
        const ms = Math.max(0, target.getTime() - now.getTime());
        const s = Math.floor(ms / 1000);
        const d = Math.floor(s / 86400);
        const h = Math.floor((s % 86400) / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        cd.textContent = d + 'd ' + String(h).padStart(2, '0') + 'h ' + String(m).padStart(2, '0') + 'm ' + String(sec).padStart(2, '0') + 's';
      };
      compute();
      setInterval(compute, 1000);
    }
  }

  // -----------------------------------------------------------------
  // Calculator — sliders + math.
  // -----------------------------------------------------------------
  function initCalculator() {
    const calc = $('[data-bv-calculator]');
    if (!calc) return;
    const inputs = {
      products: $('[data-bv-slider="products"]', calc),
      price: $('[data-bv-slider="price"]', calc),
      sales: $('[data-bv-slider="sales"]', calc),
    };
    const feesPct = parseFloat(calc.dataset.bvFees || '0.12');
    const compounding = parseFloat(calc.dataset.bvCompounding || '0.2');
    const years = parseInt(calc.dataset.bvYears || '5', 10);
    const vaultCents = parseInt(calc.dataset.bvVaultCents || '2700', 10);
    const out = {
      gross: $('[data-bv-out="gross"]', calc),
      fees: $('[data-bv-out="fees"]', calc),
      monthly: $('[data-bv-out="monthly"]', calc),
      annual: $('[data-bv-out="annual"]', calc),
      fiveYear: $('[data-bv-out="fiveYear"]', calc),
      multiple: $('[data-bv-out="multiple"]', calc),
    };
    const fills = $$('[data-bv-slider-fill]', calc);
    const valueLabels = $$('[data-bv-slider-value]', calc);
    const ccy = (window.BV && window.BV.shopCurrency) || 'USD';
    function recompute() {
      const p = parseFloat(inputs.products.value);
      const pr = parseFloat(inputs.price.value);
      const s = parseFloat(inputs.sales.value);
      const gross = p * pr * s;
      const fees = gross * feesPct;
      const monthly = gross - fees;
      const annual = monthly * 12;
      let fiveYear = 0;
      for (let i = 0; i < years; i++) fiveYear += annual * Math.pow(1 + compounding, i);
      const multiple = fiveYear / (vaultCents / 100);
      if (out.gross) out.gross.textContent = formatMoney(gross * 100, ccy);
      if (out.fees) out.fees.textContent = '-' + formatMoney(fees * 100, ccy);
      if (out.monthly) out.monthly.textContent = formatMoney(monthly * 100, ccy);
      if (out.annual) out.annual.textContent = formatMoney(annual * 100, ccy);
      if (out.fiveYear) out.fiveYear.textContent = formatMoney(fiveYear * 100, ccy);
      if (out.multiple) out.multiple.textContent = Math.round(multiple) + '×';
    }
    Object.entries(inputs).forEach(([key, input]) => {
      if (!input) return;
      const updateSlider = () => {
        const min = parseFloat(input.min);
        const max = parseFloat(input.max);
        const pct = ((parseFloat(input.value) - min) / (max - min)) * 100;
        const fill = fills.find((f) => f.dataset.bvSliderFill === key);
        const label = valueLabels.find((l) => l.dataset.bvSliderValue === key);
        if (fill) fill.style.width = pct + '%';
        if (label) {
          const prefix = input.dataset.bvPrefix || '';
          const suffix = input.dataset.bvSuffix || '';
          label.textContent = prefix + input.value + suffix;
        }
      };
      input.addEventListener('input', () => { updateSlider(); recompute(); });
      updateSlider();
    });
    recompute();
  }

  // -----------------------------------------------------------------
  // Progress bars — fill on intersect.
  // -----------------------------------------------------------------
  function initProgressBars() {
    if (!('IntersectionObserver' in window)) return;
    $$('[data-bv-progress]').forEach((el) => {
      const sold = parseInt(el.dataset.bvProgress, 10);
      const cap = parseInt(el.dataset.bvProgressCap, 10);
      if (!isFinite(sold) || !isFinite(cap) || cap === 0) return;
      const pct = Math.round((sold / cap) * 100);
      const bar = $('[data-bv-progress-bar]', el);
      if (!bar) return;
      bar.style.width = '0%';
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            bar.style.transition = 'width 1.2s cubic-bezier(0.21,0.47,0.32,0.98)';
            requestAnimationFrame(() => (bar.style.width = pct + '%'));
            io.unobserve(entry.target);
          });
        },
        { threshold: 0.4 },
      );
      io.observe(el);
    });
  }

  // -----------------------------------------------------------------
  // FAQ accordion.
  // -----------------------------------------------------------------
  function initFaq() {
    $$('[data-bv-faq-item]').forEach((item) => {
      const trigger = $('[data-bv-faq-trigger]', item);
      const content = $('[data-bv-faq-content]', item);
      if (!trigger || !content) return;
      const open = item.dataset.bvFaqOpen === '1';
      if (open) {
        content.style.maxHeight = content.scrollHeight + 'px';
        item.classList.add('bv-faq-open');
      } else {
        content.style.maxHeight = '0';
      }
      content.style.overflow = 'hidden';
      content.style.transition = 'max-height 0.35s ease, padding 0.35s ease';
      trigger.addEventListener('click', () => {
        const isOpen = item.classList.contains('bv-faq-open');
        if (isOpen) {
          content.style.maxHeight = '0';
          item.classList.remove('bv-faq-open');
        } else {
          content.style.maxHeight = content.scrollHeight + 'px';
          item.classList.add('bv-faq-open');
        }
      });
    });
  }

  // -----------------------------------------------------------------
  // Order bump — toggle Sprint upsell on checkout form.
  // -----------------------------------------------------------------
  function initOrderBump() {
    const form = $('[data-bv-checkout-form]');
    if (!form) return;
    const bump = $('[data-bv-bump-checkbox]', form);
    const bumpInput = $('[data-bv-bump-input]', form);
    const totalLabel = $('[data-bv-cta-total]', form);
    const subLabel = $('[data-bv-cta-sub]', form);
    const checkmark = $('[data-bv-bump-check]', form);
    const label = $('[data-bv-bump-label]', form);
    const BV = window.BV || {};
    const vaultCents = BV.vaultPriceCents || 2700;
    const bumpCents = BV.bumpPriceCents || 3700;
    const currency = BV.shopCurrency || 'USD';
    function update() {
      const on = bump && bump.checked;
      if (bumpInput) bumpInput.value = on ? '1' : '';
      if (checkmark) {
        checkmark.style.background = on ? 'var(--color-magenta)' : '';
        checkmark.style.borderColor = on ? 'var(--color-magenta)' : '';
        const svg = checkmark.querySelector('svg');
        if (svg) svg.style.display = on ? '' : 'none';
      }
      if (label) {
        label.style.borderColor = on ? 'var(--color-magenta)' : '';
        label.style.background = on ? 'rgba(255,61,127,0.06)' : '';
      }
      const total = on ? vaultCents + bumpCents : vaultCents;
      if (totalLabel) totalLabel.textContent = formatMoney(total, currency);
      if (subLabel) subLabel.textContent = on ? '(Vault + Sprint)' : 'one-time · lifetime';
    }
    if (bump) bump.addEventListener('change', update);
    update();
  }

  // -----------------------------------------------------------------
  // CTA tracking.
  // -----------------------------------------------------------------
  function initCtaTracking() {
    document.addEventListener('click', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const trigger = t.closest('[data-track-cta]');
      if (!trigger) return;
      const label = trigger.getAttribute('data-track-cta');
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(
            '/apps/bv-track',
            JSON.stringify({ type: 'cta_click', label, path: location.pathname }),
          );
        }
      } catch (e) {}
    });
  }

  // -----------------------------------------------------------------
  // Hero underline SVG path-draw — small subtle effect.
  // -----------------------------------------------------------------
  function initHeroUnderline() {
    $$('[data-bv-underline-path]').forEach((path) => {
      try {
        const length = path.getTotalLength();
        path.style.strokeDasharray = length;
        path.style.strokeDashoffset = length;
        path.style.transition = 'stroke-dashoffset 1.2s ease-out 0.5s';
        requestAnimationFrame(() => {
          path.style.strokeDashoffset = '0';
        });
      } catch (e) { /* getTotalLength can throw before paint */ }
    });
  }

  // -----------------------------------------------------------------
  // Inject the few CSS bits we need from JS.
  // -----------------------------------------------------------------
  function injectStyles() {
    const css = `
      @keyframes bvFadeUp {
        from { opacity: 0; transform: translateY(20px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes bvSpin { to { transform: rotate(360deg); } }
      [data-bv-filter].bv-filter-active {
        background: var(--color-ink) !important;
        color: var(--color-bg) !important;
        border-color: var(--color-ink) !important;
      }
      [data-bv-faq-content] { max-height: 0; overflow: hidden; }
      .bv-faq-open [data-bv-faq-trigger] [data-bv-faq-icon] { transform: rotate(180deg); }
      [data-bv-faq-icon] { transition: transform 0.3s ease; }
    `;
    const style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);
  }

  // -----------------------------------------------------------------
  // Bootstrap.
  // -----------------------------------------------------------------
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function safe(name, fn) {
    try { fn(); } catch (err) {
      if (window.console && console.warn) console.warn('[bv:init]', name, err);
    }
  }

  // Inject styles synchronously so animations can use them on first paint.
  injectStyles();

  ready(() => {
    safe('mobileMenu', initMobileMenu);
    safe('nav', initNav);
    safe('marquees', initMarquees);
    safe('scrollProgress', initScrollProgress);
    safe('stickyMobileCta', initStickyMobileCta);
    safe('fadeIn', initFadeIn);
    safe('heroUnderline', initHeroUnderline);
    safe('floatingMockups', initFloatingMockups);
    safe('numberTickers', initNumberTickers);
    safe('bigDomino', initBigDomino);
    safe('showcase', initShowcase);
    safe('timeMath', initTimeMath);
    safe('dropSystem', initDropSystem);
    safe('calculator', initCalculator);
    safe('progressBars', initProgressBars);
    safe('faq', initFaq);
    safe('orderBump', initOrderBump);
    safe('ctaTracking', initCtaTracking);
  });
})();
